import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { MessageSquarePlus, Trash2, Eye, EyeOff } from "lucide-react";

/** Notes attached to a master tracking code or to an individual box/label code. */
export const PackageNotes = ({ code, label }) => {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const [notes, setNotes] = useState([]);
  const [texto, setTexto] = useState("");
  const [publica, setPublica] = useState(true);
  const [busy, setBusy] = useState(false);
  const canDelete = ["admin", "operaciones"].includes(user.role);

  const load = useCallback(() => {
    api.get("/package-notes", { params: { code } }).then(r => setNotes(r.data || [])).catch(() => {});
  }, [api, code]);
  useEffect(() => { if (code) load(); }, [code, load]);

  const add = async (e) => {
    e.preventDefault();
    if (!texto.trim()) return;
    setBusy(true);
    try {
      await api.post("/package-notes", { code, texto, publica });
      setTexto(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/package-notes/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`notes-${code}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          {t("package_notes")} · <span className="font-mono normal-case text-slate-700">{label || code}</span>
        </p>
        <span className="text-[10px] text-slate-400">{notes.length}</span>
      </div>

      <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 mb-2">
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={t("add_note_placeholder")}
          data-testid={`note-input-${code}`}
          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600"
        />
        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 select-none whitespace-nowrap">
          <input type="checkbox" checked={publica} onChange={(e) => setPublica(e.target.checked)} data-testid={`note-public-${code}`} className="w-4 h-4 accent-emerald-600" />
          {publica ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {t("note_public")}
        </label>
        <button type="submit" disabled={busy} data-testid={`note-submit-${code}`} className="bg-slate-900 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2 rounded-lg flex items-center justify-center gap-1.5">
          <MessageSquarePlus className="w-3.5 h-3.5" /> {t("add")}
        </button>
      </form>

      {notes.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic">— {t("no_notes")} —</p>
      ) : (
        <ul className="space-y-1.5">
          {notes.map(n => (
            <li key={n.id} className="flex items-start gap-2 text-xs bg-slate-50 rounded-md p-2" data-testid={`note-${n.id}`}>
              <span className={`mt-0.5 shrink-0 ${n.publica ? "text-emerald-600" : "text-slate-400"}`}>
                {n.publica ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-slate-700">{n.texto}</p>
                <p className="text-[10px] text-slate-400 tabular-nums">
                  {new Date(n.created_at).toLocaleString()}{n.created_by_name ? ` · ${n.created_by_name}` : ""}
                </p>
              </div>
              {canDelete && (
                <button onClick={() => del(n.id)} data-testid={`note-del-${n.id}`} className="p-1 text-slate-400 hover:text-red-600 rounded shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
